import React, { useState } from 'react';
import { useGenres } from '../hooks/useContent';

interface FilterPanelProps {
  selectedGenre?: string;
  selectedYear?: number;
  onGenreChange: (genre: string | undefined) => void;
  onYearChange: (year: number | undefined) => void;
  className?: string;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  selectedGenre,
  selectedYear,
  onGenreChange,
  onYearChange,
  className = '',
}) => {
  const { data: genres } = useGenres();
  const [isExpanded, setIsExpanded] = useState(false);

  // Generate year options (current year back to 1900)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1899 }, (_, i) => currentYear - i);

  const handleGenreChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onGenreChange(value === '' ? undefined : value);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onYearChange(value === '' ? undefined : parseInt(value));
  };

  const clearFilters = () => {
    onGenreChange(undefined);
    onYearChange(undefined);
  };

  const hasActiveFilters = selectedGenre || selectedYear;

  return (
    <div className={`bg-gray-800 rounded-lg p-4 ${className}`}>
      {/* Mobile Toggle */}
      <div className="md:hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full text-white font-medium"
        >
          <span>Filters</span>
          <svg
            className={`w-5 h-5 transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Filter Content */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block mt-4 md:mt-0`}>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Genre Filter */}
          <div className="flex-1">
            <label htmlFor="genre-select" className="block text-sm font-medium text-gray-300 mb-2">
              Genre
            </label>
            <select
              id="genre-select"
              value={selectedGenre || ''}
              onChange={handleGenreChange}
              className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Genres</option>
              {genres && Array.isArray(genres) ? genres.map((genre: string) => (
                <option key={genre} value={genre}>
                  {genre}
                </option>
              )) : null}
            </select>
          </div>

          {/* Year Filter */}
          <div className="flex-1">
            <label htmlFor="year-select" className="block text-sm font-medium text-gray-300 mb-2">
              Year
            </label>
            <select
              id="year-select"
              value={selectedYear || ''}
              onChange={handleYearChange}
              className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Years</option>
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white border border-gray-600 hover:border-gray-500 rounded-lg transition-colors duration-200"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mt-4 flex flex-wrap gap-2">
            {selectedGenre && (
              <span className="inline-flex items-center px-3 py-1 bg-red-600 text-white text-sm rounded-full">
                {selectedGenre}
                <button
                  onClick={() => onGenreChange(undefined)}
                  className="ml-2 hover:text-gray-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
            {selectedYear && (
              <span className="inline-flex items-center px-3 py-1 bg-red-600 text-white text-sm rounded-full">
                {selectedYear}
                <button
                  onClick={() => onYearChange(undefined)}
                  className="ml-2 hover:text-gray-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterPanel;